import { useState, useRef, useEffect } from 'react';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useKasa } from '../../context/KasaContext';
import YeniRezervasyonModal from '../../components/YeniRezervasyonModal';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import DatePicker from '../../components/DatePicker';
import YilDropdown from '../../components/YilDropdown';
import UsageInfo from '../../components/UsageInfo';

const AYLAR      = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
const AYLAR_UZUN = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const GUNLER     = ['Pzt','Sal','Çar','Per','Cum','Cts','Pzr'];
const GUN_ADLARI = ['Pazar','Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi'];

function isoToDP(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function dpToISO(dp) {
  if (!dp) return '';
  const [d,m,y] = dp.split('.');
  return `${y}-${m}-${d}`;
}
function tarihStr(d) {
  return String(d.getDate()).padStart(2,'0') + '.' +
    String(d.getMonth()+1).padStart(2,'0') + '.' + d.getFullYear();
}
function isoStr(d) {
  return d.getFullYear() + '-' +
    String(d.getMonth()+1).padStart(2,'0') + '-' +
    String(d.getDate()).padStart(2,'0');
}
function parseISO(s) {
  if (!s) return null;
  const [y,m,g] = s.slice(0,10).split('-').map(Number);
  return new Date(y, m-1, g);
}
function gunAdi(d) { return GUNLER[(d.getDay()+6)%7]; }
function gunDizisi(bas, bit) {
  const gunler = []; const cur = new Date(bas);
  while (cur <= bit) { gunler.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
  return gunler;
}
function geceHesapla(giris, cikis) {
  if (!giris || !cikis) return 0;
  return Math.round((new Date(cikis) - new Date(giris)) / 86400000);
}
function rezervasyonTip(r) {
  if (r.durum?.includes('C-OUT')) return 'cikis';
  if (r.odaCheckInYapildi) return 'konaklayan';
  return 'gelecek';
}
function mapRez(r, odaList = []) {
  const odaNo = r.odaNo?.toString()
    || r.oda?.odaNo?.toString()
    || odaList.find(o => o.odaId === r.odaId)?.odaNo?.toString()
    || null;
  // Belirsiz çıkışlı ama kilitli gün sayısı varsa efektif bitiş tarihi hesapla
  const kilitGun = r.belirsizCikisKilitGun || null;
  let efektifCikis = r.cikisTarihi?.slice(0,10) || null;
  if (!r.cikisTarihi && kilitGun && r.girisTarihi) {
    const girisDate = new Date(r.girisTarihi.slice(0,10));
    girisDate.setDate(girisDate.getDate() + kilitGun);
    efektifCikis = isoStr(girisDate);
  }
  return {
    rezNo:         r.rezNo,
    rezervasyonId: r.rezervasyonId,
    odaNo,
    odaId:         r.odaId || null,
    misafir:       r.rezervasyonMisafirler?.map(m => m.misafir?.adSoyad).filter(Boolean).join(' / ') || '-',
    giris:         r.girisTarihi?.slice(0,10),
    cikis:         efektifCikis,
    belirsiz:      !r.cikisTarihi,
    gece:          geceHesapla(r.girisTarihi, r.cikisTarihi),
    acente:        r.acenteAd || r.acente?.acenteAd || '',
    durum:         r.durum || '',
    tip:           rezervasyonTip(r),
  };
}

// ── Ayarlar Modal (bottom sheet mobilde) ──
function AyarlarModal({ onClose, mevcutRenkler, onKaydet, mevcutOncekiGun, mevcutSonrakiGun, onTarihGuncelle, mevcutFiyatGoster, onFiyatGosterDegis }) {
  const [renkler, setRenkler]       = useState(mevcutRenkler);
  const [oncekiGun, setOncekiGun]   = useState(mevcutOncekiGun);
  const [sonrakiGun, setSonrakiGun] = useState(mevcutSonrakiGun);
  const [fiyatGoster, setFiyatGoster] = useState(mevcutFiyatGoster);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-end sm:items-center justify-center sm:px-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white/95 backdrop-blur-xl w-full max-w-2xl rounded-t-[2.5rem] sm:rounded-[3rem] shadow-2xl border border-white/40 overflow-hidden animate-modal-in transform-gpu"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 px-8 py-8 flex items-center justify-between shadow-lg shadow-indigo-100/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white text-2xl shadow-inner backdrop-blur-sm">⚙️</div>
            <div>
              <h3 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Çizelge Ayarları</h3>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">Görünüm ve Filtre Tercihleri</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="p-8 space-y-8 overflow-y-auto max-h-[70vh] custom-scrollbar text-left">
          {/* Renkler */}
          <div className="space-y-4">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Rezervasyon Durum Renkleri</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: 'cikis',      label: 'Çıkış Yapmış', icon: '🏃' },
                { key: 'konaklayan', label: 'Konaklayan',  icon: '🛌' },
                { key: 'gelecek',    label: 'Gelecek Rez.', icon: '📅' },
              ].map(item => (
                <div key={item.key} className="relative group">
                   <div className="flex items-center gap-3 p-3 rounded-2xl border-2 border-gray-50 bg-gray-50/30 hover:bg-white hover:border-indigo-100 transition-all cursor-pointer overflow-hidden"
                     style={{ borderLeftColor: renkler[item.key] }}>
                     <input type="color" value={renkler[item.key]}
                        onChange={e => setRenkler(r => ({...r, [item.key]: e.target.value}))}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"/>
                     <span className="text-lg">{item.icon}</span>
                     <div>
                       <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Durum</p>
                       <p className="text-xs font-bold text-gray-700">{item.label}</p>
                     </div>
                   </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tarih aralığı */}
          <div className="space-y-4">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Takvim Görüntüleme Aralığı</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label:'Bugünden Geriye Kaç Gün?', val:oncekiGun, set:setOncekiGun, icon:'⏪' },
                { label:'Bugünden İleriye Kaç Gün?',  val:sonrakiGun, set:setSonrakiGun, icon:'⏩' },
              ].map(item => (
                <div key={item.label} className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">{item.icon}</span>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.label}</label>
                  </div>
                  <div className="flex items-center bg-white rounded-xl border-2 border-gray-100 overflow-hidden shadow-sm focus-within:border-indigo-400 transition-all">
                    <button onClick={() => item.set(v => Math.max(0,v-1))} className="w-12 h-12 flex items-center justify-center text-gray-400 hover:bg-gray-50 active:bg-gray-100 font-bold border-r border-gray-100 transition-colors">−</button>
                    <span className="flex-1 text-center font-black text-gray-800 tracking-tighter">{item.val} Gün</span>
                    <button onClick={() => item.set(v => v+1)} className="w-12 h-12 flex items-center justify-center text-gray-400 hover:bg-gray-50 active:bg-gray-100 font-bold border-l border-gray-100 transition-colors">+</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fiyat göster */}
          <div className="bg-white rounded-2xl border-2 border-gray-50 p-6 flex items-center justify-between hover:border-indigo-100 transition-all shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-xl shadow-inner">💰</div>
              <div>
                <p className="text-sm font-black text-gray-800 tracking-tighter leading-none mb-1">Dinamik Fiyatlar</p>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest opacity-70">Müsait odalarda günlük fiyat göster</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={fiyatGoster} onChange={() => setFiyatGoster(!fiyatGoster)} className="sr-only peer"/>
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-6 after:transition-all peer-checked:bg-indigo-600 transition-colors shadow-inner"></div>
            </label>
          </div>
        </div>

        <div className="px-8 py-8 bg-gray-50 border-t border-gray-100 flex gap-4">
          <button onClick={onClose} className="flex-1 h-16 border-2 border-gray-200 bg-white hover:bg-gray-50 text-gray-500 rounded-2xl font-black uppercase tracking-widest text-[11px] transition-all active:scale-95 leading-none">İptal</button>
          <button onClick={() => { onKaydet(renkler); onTarihGuncelle(oncekiGun, sonrakiGun); onFiyatGosterDegis(fiyatGoster); onClose(); }}
            className="flex-[2] h-16 bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[11px] shadow-xl shadow-indigo-100 transition-all active:scale-95 leading-none">
            💾 TERCİHLERİ KAYDET
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Rezervasyon Detay Sheet (mobil touch-friendly) ────────────
function RezervasyonDetaySheet({ rez, onClose, onDetay }) {
  if (!rez) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[200] flex items-end justify-center transform-gpu" onClick={onClose}>
      <div className="bg-white/95 backdrop-blur-xl w-full max-w-lg rounded-t-[2.5rem] shadow-2xl border border-white/40 overflow-hidden animate-modal-in transform-gpu"
        onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full"/>
        </div>
        <div className="px-5 pt-2 pb-4">
          <p className="text-xs text-gray-400 font-medium mb-1">REZ. NO #{rez.rezNo}</p>
          <p className="text-xl font-black text-gray-800 mb-4">{rez.misafir !== '-' ? rez.misafir : 'Misafir bilgisi yok'}</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-50 rounded-2xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Oda</p>
              <p className="text-base font-bold text-gray-800">{rez.odaNo || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Gece</p>
              <p className="text-base font-bold text-gray-800">{rez.gece} gece</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Giriş</p>
              <p className="text-sm font-bold text-gray-800">{rez.giris ? tarihStr(parseISO(rez.giris)) : '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Çıkış</p>
              <p className="text-sm font-bold text-gray-800">{rez.cikis ? tarihStr(parseISO(rez.cikis)) : '—'}</p>
            </div>
          </div>
          {rez.acente && (
            <div className="bg-gray-50 rounded-2xl p-3 mb-4">
              <p className="text-xs text-gray-400 mb-0.5">Acente</p>
              <p className="text-sm font-semibold text-gray-800">{rez.acente}</p>
            </div>
          )}
          <button onClick={() => { onDetay(rez); onClose(); }}
            className="w-full bg-indigo-600 active:bg-indigo-700 text-white py-3.5 rounded-2xl text-sm font-bold mb-2 transition-colors">
            Rezervasyon Detayı
          </button>
          <button onClick={onClose}
            className="w-full bg-gray-100 active:bg-gray-200 text-gray-600 py-3.5 rounded-2xl text-sm font-semibold transition-colors">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Yeni Giriş Sheet (mobil) ──────────────────────────────────
function YeniGirisSheet({ info, onClose, onYeniGiris }) {
  if (!info) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[200] flex items-end justify-center transform-gpu" onClick={onClose}>
      <div className="bg-white/95 backdrop-blur-xl w-full max-w-lg rounded-t-[2.5rem] shadow-2xl border border-white/40 overflow-hidden animate-modal-in transform-gpu"
        onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full"/>
        </div>
        <div className="px-5 pt-2 pb-6">
          <p className="text-lg font-black text-gray-800 mb-1">Yeni Rezervasyon</p>
          <p className="text-sm text-gray-500 mb-5">
            Oda <span className="font-bold text-gray-700">{info.odaNo}</span> — {tarihStr(parseISO(info.tarih))}
          </p>
          <button onClick={() => { onYeniGiris(info); onClose(); }}
            className="w-full bg-blue-600 active:bg-blue-700 text-white py-3.5 rounded-2xl text-sm font-bold mb-2 transition-colors">
            Rezervasyon Oluştur
          </button>
          <button onClick={onClose}
            className="w-full bg-gray-100 active:bg-gray-200 text-gray-600 py-3.5 rounded-2xl text-sm font-semibold transition-colors">
            İptal
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RenkKutusu ────────────────────────────────────────────────
function RenkKutusu({ renk, onChange, label, sayi }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 text-sm cursor-pointer hover:border-gray-400 active:bg-gray-50 transition-colors select-none relative overflow-hidden">
      <input type="color" value={renk} onChange={e => onChange(e.target.value)} 
        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"/>
      <span className="w-5 h-5 rounded border border-gray-300 flex-shrink-0 block" style={{ backgroundColor: renk }}/>
      <span className="font-semibold text-gray-700 whitespace-nowrap">{label} - {sayi}</span>
    </div>
  );
}

// ── Tooltip (sadece masaüstü) ─────────────────────────────────
function TooltipBox({ rez, oda, gun, x, y }) {
  const leftPos = Math.min(x-88, window.innerWidth-190);
  const topPos = y-15;
  return (
    <div className="pointer-events-none hidden sm:block animate-fade-in" style={{ position:'fixed', top:topPos, left:leftPos, zIndex:9999, transform:'translateY(-100%)' }}>
      <div className="bg-slate-900/95 backdrop-blur-xl text-white rounded-2xl p-5 shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/10 min-w-[200px]">
        {rez ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 border-b border-white/10 pb-3">
               <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-lg">👤</div>
               <div className="flex-1">
                 <p className="text-[10px] font-black text-white/40 uppercase tracking-widest leading-none mb-1">Misafir</p>
                 <h4 className="font-black text-sm tracking-tight truncate max-w-[140px] uppercase">{rez.misafir && rez.misafir !== '-' ? rez.misafir : 'İsimsiz Misafir'}</h4>
               </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
               <div>
                 <p className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">Giriş</p>
                 <p className="font-bold text-[11px]">{rez.giris ? tarihStr(parseISO(rez.giris)) : '-'}</p>
               </div>
               <div>
                 <p className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">Çıkış</p>
                 <p className="font-bold text-[11px]">{rez.cikis ? tarihStr(parseISO(rez.cikis)) : '-'}</p>
               </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
               <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded-md text-[9px] font-black uppercase tracking-widest">{rez.gece} GECE</span>
               {rez.acente && <span className="text-[9px] font-black text-white/30 truncate max-w-[80px] uppercase">@{rez.acente}</span>}
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-center">
            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-3 text-xl">✨</div>
            <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">Yeni Rezervasyon</p>
            <p className="font-black text-sm tracking-tighter">Oda {oda?.odaNo}</p>
            <p className="text-[10px] font-bold text-indigo-400">{tarihStr(gun)}</p>
          </div>
        )}
      </div>
      <div className="w-3 h-3 bg-slate-900 rotate-45 mx-auto -mt-1.5 border-r border-b border-white/10"/>
    </div>
  );
}

// ── Oda Satırı ────────────────────────────────────────────────
function OdaSatiri({ oda, gunler, rezervasyonlar, renkler, onYeniGiris, onDetay, onMobilTap, kolonGenisligi, fiyatGoster, fiyatMap, carpanMap, dovizKurlari, onTooltip }) {

  const odaRezler  = rezervasyonlar.filter(r => r.odaNo === oda.odaNo?.toString());
  const kirliMi    = oda.durum === 'Kirli';
  const doluMu     = odaRezler.some(r => r.durum === 'C-IN Yapıldı');
  const arizaliMi  = !!oda.arizaliMi;
  const kapalimi   = kirliMi || arizaliMi; // doluMu tıklamayı engellemez
  const grupKapali = oda.aktifMi === false || oda.grupAktifMi === false || oda.odaTipiAktifMi === false || !oda.odaTipiId || !oda.odaGrubuId;

  const odaKapaliBg = (kirliMi && arizaliMi) ? '#fed7aa'
                    : kirliMi   ? '#fee2e2'
                    : arizaliMi ? '#fef3c7'
                    : doluMu    ? '#bfdbfe'
                    : null;

  function rezBul(iso) {
    return odaRezler.find(r => {
      if (!r.giris || r.giris > iso) return false;
      // Belirsiz çıkış + kilit günü yok → sonsuza kadar aktif
      if (r.belirsiz && !r.cikis) return true;
      // Belirsiz çıkış + kilit günü var → efektif cikis kullan
      return r.cikis ? r.cikis > iso : iso === r.giris;
    });
  }

  function gunFiyatHesapla(gun) {
    const fiyatlar = fiyatMap[oda.odaTipiId];
    if (!fiyatlar) return null;
    const iso = isoStr(gun);
    const ozel = fiyatlar.ozelFiyatlar?.find(f => f.bas <= iso && f.bit >= iso);
    const bazFiyat = ozel ? ozel.fiyat : (() => {
      const gAdi = GUN_ADLARI[gun.getDay()];
      return fiyatlar.gunlukFiyatlar?.find(f => f.gun === gAdi)?.fiyat ?? null;
    })();
    if (bazFiyat == null) return null;
    const carpan = carpanMap?.[oda.odaTipiId]?.carpan || 1;
    const deger = carpan > 1 ? Math.round(bazFiyat * carpan) : bazFiyat;
    const pb = ozel?.paraBirimi || fiyatlar.paraBirimi || 'TL';
    return { deger, pb };
  }

  const tipCarpan = carpanMap?.[oda.odaTipiId];
  const carpanAktif = tipCarpan?.carpan > 1;

  function hucreRengi(rez) {
    if (!rez) return null;
    if (rez.tip === 'konaklayan') return renkler.konaklayan;
    if (rez.tip === 'gelecek')   return renkler.gelecek;
    return renkler.cikis;
  }

  return (
    <tr className={`group/row border-b border-gray-100 hover:bg-indigo-50/20 transition-all duration-300 ${grupKapali ? 'opacity-70' : ''}`}>
      <td className={`sticky left-0 z-30 px-5 font-black text-xs border-r-2 border-slate-100 whitespace-nowrap text-slate-800 shadow-[5px_0_15px_rgba(0,0,0,0.02)] transition-colors group-hover/row:bg-indigo-50/50 ${grupKapali ? 'bg-slate-100' : 'bg-white/95 backdrop-blur-md'}`} style={{ height:'48px', minWidth:'100px' }}>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-black tracking-tighter">{oda.odaNo}</span>
            {grupKapali && <span className="text-[7px] font-black bg-slate-400 text-white px-1 py-0.5 rounded-md tracking-widest">KAPALI</span>}
          </div>
          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 flex-wrap">
            {kapalimi ? (
              <div className="flex items-center gap-1">
                {kirliMi   && <span className="text-rose-500 animate-pulse" title="Kirli">●</span>}
                {arizaliMi && <span className="text-amber-500 animate-pulse" title="Arızalı">●</span>}
                <span className="text-slate-500">{oda.odaTipiAd?.split(' ')[0]}</span>
              </div>
            ) : (
              <span className="truncate max-w-[70px] leading-none">{oda.odaTipiAd}</span>
            )}
          </div>
        </div>
      </td>
      {gunler.map(gun => {
        const iso = isoStr(gun);
        const rez = rezBul(iso);
        const haftaSonu = gun.getDay()===0 || gun.getDay()===6;
        const renk = hucreRengi(rez);
        const tooltipKey = oda.odaNo + '-' + iso;

        const oncekiISO  = isoStr(new Date(gun.getTime()-86400000));
        const sonrakiISO = isoStr(new Date(gun.getTime()+86400000));
        const oncekiRez  = rezBul(oncekiISO);
        const sonrakiRez = rezBul(sonrakiISO);
        const solDevam = rez && oncekiRez && rez.rezNo === oncekiRez.rezNo;
        const sagDevam = rez && sonrakiRez && rez.rezNo === sonrakiRez.rezNo;
        const br = rez ? (
          (solDevam?'0':'6px') + ' ' + (sagDevam?'0':'6px') + ' ' + (sagDevam?'0':'6px') + ' ' + (solDevam?'0':'6px')
        ) : '0';

        const rezGirisMilis = rez ? parseISO(rez.giris)?.getTime() : 0;
        const rezCikisMilis = rez ? parseISO(rez.cikis)?.getTime() : 0;
        const rezGunSayisi  = rez ? Math.round((rezCikisMilis-rezGirisMilis)/86400000) : 0;
        const rezOrtaGun    = rez ? isoStr(new Date(rezGirisMilis + Math.floor(rezGunSayisi/2)*86400000)) : null;
        const isOrta = rez && iso === rezOrtaGun;

        const fiyat = !rez && fiyatGoster && !kapalimi && !grupKapali ? gunFiyatHesapla(gun) : null;

        const grupKapaliBg = grupKapali && !rez
          ? 'repeating-linear-gradient(135deg,#e5e7eb 0px,#e5e7eb 1px,#f3f4f6 1px,#f3f4f6 6px)'
          : undefined;

        return (
          <td key={iso}
            className={`text-center py-0 px-0 border-r border-slate-100 relative group/cell ${rez ? 'z-10' : ''} ${rez ? 'cursor-pointer' : (kapalimi || grupKapali) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              height:'48px', minWidth:kolonGenisligi+'px',
              background: grupKapaliBg || (!rez && kapalimi ? odaKapaliBg : !rez && doluMu ? '#eff6ff' : undefined),
              backgroundColor: !grupKapaliBg && !rez && !kapalimi && !doluMu && haftaSonu ? '#fcfdfe' : undefined,
            }}
            onMouseEnter={e => {
              const rect = e.currentTarget.getBoundingClientRect();
              onTooltip({ rez, oda, gun, x: rect.left + rect.width / 2, y: rect.top });
            }}
            onMouseLeave={() => onTooltip(null)}
            onClick={() => {
              const isMobile = window.innerWidth < 640;
              if (rez) {
                if (isMobile) onMobilTap({ type: 'rez', rez });
                else onDetay(rez);
                return;
              }
              if (kapalimi || grupKapali) return;
              const info = { odaId: oda.odaId, odaNo: oda.odaNo, tarih: iso };
              if (isMobile) onMobilTap({ type: 'yeni', info });
              else onYeniGiris(info);
            }}
          >
            {rez ? (
              <div className="h-[36px] mt-[6px] mb-[6px] flex items-center justify-center font-black text-slate-800 text-[9px] uppercase tracking-tighter overflow-hidden transition-all group-hover/cell:brightness-95 group-hover/cell:scale-[1.02] shadow-sm select-none"
                style={{ backgroundColor:renk, borderRadius:br }}>
                <span className="truncate px-2 max-w-full leading-none">
                  {isOrta && rez.misafir && rez.misafir!=='-' ? rez.misafir : ''}
                </span>
              </div>
            ) : kapalimi ? (
              <div className="h-full flex items-center justify-center gap-1 opacity-40 group-hover/cell:opacity-100 transition-opacity" style={{ height:'48px' }}>
                {kirliMi   && <span className="text-sm">🧹</span>}
                {arizaliMi && <span className="text-sm">🔧</span>}
              </div>
            ) : grupKapali ? (
              <div style={{ height:'48px' }}/>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-300 group-hover/cell:text-indigo-500 font-black transition-all group-hover/cell:bg-indigo-50/50" style={{ height:'48px' }}>
                {fiyat != null ? (
                  <span className={`text-[9px] tracking-tight leading-none ${carpanAktif ? 'text-orange-500' : 'text-slate-300'}`}>
                    {carpanAktif && <span className="mr-0.5">🔥</span>}
                    {fiyat.deger.toLocaleString('tr-TR')} {fiyat.pb}
                  </span>
                ) : <span className="text-[14px] opacity-20 transition-opacity group-hover/cell:opacity-100">+</span>}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────
export default function BlokajCizelge() {
  const { hasYetki } = useAuth();
  const { dovizKurlari } = useKasa();
  const bugun = new Date();
  const [globalTooltip, setGlobalTooltip] = useState(null);

  const [baslangicTarih, setBaslangicTarih] = useState(isoStr(new Date(bugun.getFullYear(), bugun.getMonth(), 1)));
  const [bitisTarih, setBitisTarih]         = useState(isoStr(new Date(bugun.getFullYear(), bugun.getMonth()+1, 0)));
  const [ayarlarAcik, setAyarlarAcik]   = useState(false);
  const [oncekiGun, setOncekiGun]       = useState(2);
  const [sonrakiGun, setSonrakiGun]     = useState(20);
  const [fiyatGoster, setFiyatGoster]   = useState(true);
  const [yeniGirisModal, setYeniGirisModal] = useState(null);
  const [detayRez, setDetayRez]         = useState(null);
  const [mobilSheet, setMobilSheet]     = useState(null);

  // Veri
  const [odalar, setOdalar]             = useState([]);
  const [rezervasyonlar, setRezervasyonlar] = useState([]);
  const [fiyatMap, setFiyatMap]         = useState({});
  const [carpanMap, setCarpanMap]       = useState({});
  const [yukleniyor, setYukleniyor]     = useState(true);
  const [hata, setHata]                 = useState(null);

  const [renkler, setRenkler] = useState({
    konaklayan: '#22d3ee',
    gelecek:    '#4ade80',
    cikis:      '#f97316',
  });
  const renklerYuklendiRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    apiRequest('genel-ayarlar').then(data => {
      if (data) {
        setRenkler(prev => ({
          konaklayan: data.blokajKonaklayanRenk || prev.konaklayan,
          gelecek:    data.blokajGelecekRenk    || prev.gelecek,
          cikis:      data.blokajCikisRenk      || prev.cikis,
        }));
      }
    }).catch(() => {}).finally(() => { renklerYuklendiRef.current = true; });
  }, []);

  useEffect(() => {
    if (!renklerYuklendiRef.current) return;
    if (!hasYetki('gorunumAyarlariDuzenle')) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      apiRequest('genel-ayarlar/blokaj-renkleri', 'PUT', {
        blokajKonaklayanRenk: renkler.konaklayan,
        blokajGelecekRenk:    renkler.gelecek,
        blokajCikisRenk:      renkler.cikis,
      });
    }, 800);
  }, [renkler, hasYetki]);

  const fetchVeriler = async () => {
    setYukleniyor(true); setHata(null);
    try {
      const [odaData, rezData] = await Promise.all([
        apiRequest('oda'),
        apiRequest('rezervasyonlar'),
      ]);
      const odaList = (odaData || []).slice().sort((a, b) => {
        const na = parseInt(a.odaNo, 10), nb = parseInt(b.odaNo, 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return String(a.odaNo || '').localeCompare(String(b.odaNo || ''), 'tr', { numeric: true });
      });
      setOdalar(odaList);
      setRezervasyonlar((rezData || []).filter(r => r.durum !== 'İptal').map(r => mapRez(r, odaList)));

      const tipIdler = [...new Set(odaList.map(o => o.odaTipiId).filter(Boolean))];
      const fiyatSonuclari = await Promise.all(
        tipIdler.map(id => apiRequest(`odafiyat/${id}`).then(d => ({ id, data: d })).catch(() => ({ id, data: null })))
      );
      const yeniFiyatMap = {};
      fiyatSonuclari.forEach(({ id, data }) => {
        if (data) {
          yeniFiyatMap[id] = {
            paraBirimi: data.gunlukFiyatlar?.[0]?.paraBirimi || data.ozelFiyatlar?.[0]?.paraBirimi || 'TL',
            gunlukFiyatlar: data.gunlukFiyatlar || [],
            ozelFiyatlar: (data.ozelFiyatlar || []).map(f => ({
              bas: f.baslangicTarihi?.slice(0,10),
              bit: f.bitisTarihi?.slice(0,10),
              fiyat: f.fiyat,
              paraBirimi: f.paraBirimi || 'TL',
            })),
          };
        }
      });
      setFiyatMap(yeniFiyatMap);

      const bugunISO2 = isoStr(new Date());
      const yariISO   = isoStr(new Date(Date.now() + 86400000));
      const carpanSonuclari = await Promise.all(
        tipIdler.map(id =>
          apiRequest(`dolulukfiyat/carpan?odaTipiId=${id}&giris=${bugunISO2}&cikis=${yariISO}`)
            .then(d => ({ id, data: d })).catch(() => ({ id, data: null }))
        )
      );
      const yeniCarpanMap = {};
      carpanSonuclari.forEach(({ id, data }) => { if (data) yeniCarpanMap[id] = data; });
      setCarpanMap(yeniCarpanMap);
    } catch {
      setHata('Veriler yüklenemedi.');
    }
    setYukleniyor(false);
  };

  useEffect(() => { fetchVeriler(); }, []);

  function handleTarihGuncelle(once, sonra) {
    const b = new Date();
    const bas = new Date(b); bas.setDate(b.getDate()-once);
    const bit = new Date(b); bit.setDate(b.getDate()+sonra);
    setOncekiGun(once); setSonrakiGun(sonra);
    setBaslangicTarih(isoStr(bas));
    setBitisTarih(isoStr(bit));
  }

  const gunler = gunDizisi(parseISO(baslangicTarih), parseISO(bitisTarih));
  const kolonGenisligi = (() => {
    const n = gunler.length;
    if (n <= 7)  return 110;
    if (n <= 14) return 80;
    if (n <= 21) return 65;
    if (n <= 31) return 52;
    if (n <= 45) return 42;
    return 36;
  })();
  const baslikFont = kolonGenisligi >= 80 ? '13px' : kolonGenisligi >= 52 ? '11px' : '10px';
  const bugunISO   = isoStr(bugun);

  const cikisYapanlar = rezervasyonlar.filter(r => r.tip === 'cikis').length;
  const konaklayanlar = rezervasyonlar.filter(r => r.tip === 'konaklayan').length;
  const gelecekler    = rezervasyonlar.filter(r => r.tip === 'gelecek').length;

  return (
    <div className="space-y-6">
      
      {/* ── Üst Bilgi (Premium Header) ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white/60 backdrop-blur-md rounded-[3rem] p-8 shadow-sm border border-white/50 animate-fade-in">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-[1.5rem] flex items-center justify-center text-white text-3xl shadow-xl shadow-indigo-100 animate-bounce-slow">
            📊
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tighter leading-none mb-2">Doluluk Çizelgesi</h1>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Müsaitlik, Blokaj ve Takvim Yönetimi</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
           <button onClick={fetchVeriler}
             className="h-14 px-6 gap-3 flex items-center justify-center rounded-2xl border-2 border-gray-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/30 text-gray-500 hover:text-indigo-600 transition-all active:scale-95 group font-black text-[10px] uppercase tracking-widest leading-none">
             <svg className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
             </svg>
             Yenile
           </button>
           <button onClick={() => setAyarlarAcik(true)}
             className="h-14 px-6 gap-3 bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-white flex items-center justify-center rounded-2xl transition-all shadow-xl shadow-indigo-100 active:scale-95 font-black text-[10px] uppercase tracking-widest leading-none">
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="animate-spin-slow">
               <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2.5"/>
               <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="white" strokeWidth="2"/>
             </svg>
             Ayarlar
           </button>
        </div>
      </div>

      {/* ── Kontrol Paneli ── */}
      <div className="bg-white rounded-[3rem] shadow-2xl border border-white p-8 space-y-8 animate-fade-in select-none">

        {/* Satır 1: Yıl + Ay seçimi */}

        {/* Satır 2: Tarih aralığı + Renk göstergeleri */}
        <div className="flex flex-col xl:flex-row xl:items-start gap-8">
          <div className="flex flex-col gap-2">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Hızlı Tarih Görüntüle</p>
             <div className="flex items-center gap-3 bg-gray-50 rounded-2xl p-2 border-2 border-gray-100 shadow-inner">
                <DatePicker value={isoToDP(baslangicTarih)} onChange={v => setBaslangicTarih(dpToISO(v))}/>
                <span className="text-gray-300">/</span>
                <DatePicker value={isoToDP(bitisTarih)} onChange={v => setBitisTarih(dpToISO(v))}/>
             </div>
          </div>

          <div className="flex-1 flex flex-col gap-2">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 text-left">İstatistikler & Kapasite</p>
            {/* Renk göstergeler — mobilde küçük rozetler */}
            <div className="flex items-center gap-3 flex-wrap">
              {hasYetki('gorunumAyarlariDuzenle')
                ? <>
                    <RenkKutusu renk={renkler.cikis}      onChange={v => setRenkler(r => ({...r, cikis:v}))}      label="Çıkış"      sayi={cikisYapanlar} color="rose" />
                    <RenkKutusu renk={renkler.konaklayan} onChange={v => setRenkler(r => ({...r, konaklayan:v}))} label="Konaklayan" sayi={konaklayanlar} color="emerald" />
                    <RenkKutusu renk={renkler.gelecek}    onChange={v => setRenkler(r => ({...r, gelecek:v}))}    label="Gelecek"    sayi={gelecekler} color="indigo" />
                  </>
                : <>
                    {[
                      { renk: renkler.cikis,      label: 'Çıkış',      sayi: cikisYapanlar, color:'rose' },
                      { renk: renkler.konaklayan, label: 'Konaklayan', sayi: konaklayanlar, color:'emerald' },
                      { renk: renkler.gelecek,    label: 'Gelecek',    sayi: gelecekler, color:'indigo' },
                    ].map(item => (
                      <span key={item.label} className="flex items-center gap-2.5 px-4 py-3 rounded-2xl border-2 border-gray-50 text-[10px] font-black uppercase tracking-widest bg-white text-gray-700 shadow-sm transition-transform hover:-translate-y-1">
                        <span className="w-3.5 h-3.5 rounded-full shadow-sm" style={{ backgroundColor: item.renk }}/>
                        {item.label} <span className="ml-1 text-gray-400">{item.sayi}</span>
                      </span>
                    ))}
                  </>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Hata */}
      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center justify-between text-sm">
          <span>{hata}</span>
          <button onClick={fetchVeriler} className="font-bold underline">Tekrar dene</button>
        </div>
      )}

      {/* ── Tablo ── */}
      <div className="bg-white/90 backdrop-blur-md rounded-[2.5rem] shadow-2xl border border-white/50 overflow-hidden">
        {yukleniyor ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-3xl mb-2 animate-spin inline-block">⟳</div>
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling:'touch' }}>
            <table className="text-xs border-collapse" style={{ minWidth:(80+gunler.length*kolonGenisligi)+'px' }}>
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 bg-slate-900/95 backdrop-blur-md text-slate-100 text-left px-4 py-3 font-bold whitespace-nowrap border-r border-slate-700/50 shadow-sm" style={{ minWidth:'80px' }}>
                    Oda
                  </th>
                  {gunler.map(gun => {
                    const iso = isoStr(gun);
                    const haftaSonu = gun.getDay()===0 || gun.getDay()===6;
                    const bugunMu = iso === bugunISO;
                    return (
                      <th key={iso} className="text-center py-2 px-1 font-semibold border-r border-slate-700/50 text-white shadow-sm"
                        style={{ minWidth:kolonGenisligi+'px', backgroundColor: bugunMu ? '#4f46e5' : haftaSonu ? '#475569' : '#1e293b' }}>
                        <div style={{ fontSize:baslikFont }}>{gun.getDate()} {kolonGenisligi>=65 ? AYLAR_UZUN[gun.getMonth()].slice(0,3) : ''}</div>
                        <div style={{ fontSize:baslikFont, opacity:0.8 }}>{gunAdi(gun)}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {odalar.map(oda => (
                  <OdaSatiri
                    key={oda.odaId}
                    oda={oda}
                    gunler={gunler}
                    rezervasyonlar={rezervasyonlar}
                    renkler={renkler}
                    onYeniGiris={setYeniGirisModal}
                    onDetay={setDetayRez}
                    onMobilTap={setMobilSheet}
                    kolonGenisligi={kolonGenisligi}
                    fiyatGoster={fiyatGoster}
                    fiyatMap={fiyatMap}
                    carpanMap={carpanMap}
                    dovizKurlari={dovizKurlari}
                    onTooltip={setGlobalTooltip}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modaller ── */}
      {ayarlarAcik && (
        <AyarlarModal
          onClose={() => setAyarlarAcik(false)}
          mevcutRenkler={renkler}
          onKaydet={setRenkler}
          mevcutOncekiGun={oncekiGun}
          mevcutSonrakiGun={sonrakiGun}
          onTarihGuncelle={handleTarihGuncelle}
          mevcutFiyatGoster={fiyatGoster}
          onFiyatGosterDegis={setFiyatGoster}
        />
      )}

      {/* Mobil: Rezervasyon Sheet */}
      {mobilSheet?.type === 'rez' && (
        <RezervasyonDetaySheet
          rez={mobilSheet.rez}
          onClose={() => setMobilSheet(null)}
          onDetay={r => { setDetayRez(r); setMobilSheet(null); }}
        />
      )}

      {/* Mobil: Yeni Giriş Sheet */}
      {mobilSheet?.type === 'yeni' && (
        <YeniGirisSheet
          info={mobilSheet.info}
          onClose={() => setMobilSheet(null)}
          onYeniGiris={info => { setYeniGirisModal(info); setMobilSheet(null); }}
        />
      )}

      {yeniGirisModal && (
        <YeniRezervasyonModal
          onClose={() => setYeniGirisModal(null)}
          varsayilanOda={yeniGirisModal?.odaId}
          varsayilanTarih={yeniGirisModal?.tarih}
          onKaydedildi={fetchVeriler}
        />
      )}

      {detayRez && (
        <RezervasyonDetayModal
          rezervasyonId={detayRez.rezervasyonId}
          onClose={() => setDetayRez(null)}
          onGuncelle={fetchVeriler}
        />
      )}

      {/* ── Alt Bilgi ── */}
      <UsageInfo
        id="doluluk-cizelge"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="Doluluk Çizelgesi (Blokaj), otelinizin oda müsaitlik durumunu takvim bazlı görmenizi, rezervasyonları yönetmenizi ve dinamik fiyat takibi yapmanızı sağlayan ana kumanda merkezidir."
        scenarios={[
          { title: "Hızlı Rezervasyon", content: "Boş bir oda hücresine tıklayarak doğrudan o oda ve tarih için yeni bir rezervasyon oluşturabilirsiniz." },
          { title: "Görünüm Ayarları", content: "Sağ üstteki çark ikonundan rezervasyon renklerini özelleştirebilir ve takvimin kaç günlük bir aralığı göstereceğini belirleyebilirsiniz." },
          { title: "Fiyat & Müsaitlik", content: "Fiyatlar aktif edildiğinde boş odalarda o günkü satış rakamlarını görebilir, carpanlı fiyatları 🔥 simgesiyle takip edebilirsiniz." },
          { title: "Rezervasyon Yönetimi", content: "Dolu hücrelere tıklayarak misafir bilgilerine, ödeme detaylarına ve check-in işlemlerine anında erişebilirsiniz." }
        ]}
      />

      {globalTooltip && <TooltipBox {...globalTooltip} />}

      <style>{`
        @keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      `}</style>
    </div>
  );
}
